'use client';

import { LinkedInIcon, XIcon, FacebookIcon, LinkIcon } from '@/assets';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import {
  LinkedinShareButton,
  TwitterShareButton,
  FacebookShareButton,
} from 'react-share';

interface Props {
  className?: string;
  bgColor?: string;
  fill?: string;
}

const subscribe = () => () => {};

const TOAST_DURATION_MS = 2500;

/**
 * Copy text to the clipboard, resilient to environments where the async
 * Clipboard API is blocked (e.g. permission-restricted iframes): falls back to a
 * hidden-textarea + execCommand('copy'). Returns whether the copy succeeded.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export const ShareOnSocialMedia = (props: Props) => {
  // Read the current URL in an SSR-safe way: empty string on the server and the
  // first client render (so hydration matches), then the real location.
  const url = useSyncExternalStore(
    subscribe,
    () => window.location.href,
    () => '',
  );

  // Lightweight "copied" toast (no external library / no global container): a
  // portalled, bottom-centered pill that auto-dismisses. Replaces the old
  // native alert().
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const copyUrl = async () => {
    const ok = await copyToClipboard(url);
    if (!ok) {
      return;
    }
    setCopied(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setCopied(false), TOAST_DURATION_MS);
  };

  return (
    <div className={props.className}>
      <button
        className={`rounded-[50%] p-1 ${props.bgColor ? props.bgColor : 'bg-primary-light-grey'}`}
        onClick={() => copyUrl()}
        aria-label="Copy URL to clipboard"
      >
        <LinkIcon fill={`${props.fill ? props.fill : 'black'}`}></LinkIcon>
      </button>
      <LinkedinShareButton url={url}>
        <div
          className={`rounded-[50%] p-1 ${props.bgColor ? props.bgColor : 'bg-primary-light-grey'}`}
        >
          <LinkedInIcon
            fill={`${props.fill ? props.fill : 'black'}`}
          ></LinkedInIcon>
        </div>
      </LinkedinShareButton>
      <TwitterShareButton url={url}>
        <div
          className={`rounded-[50%] p-1 ${props.bgColor ? props.bgColor : 'bg-primary-light-grey'}`}
        >
          <XIcon fill={`${props.fill ? props.fill : 'black'}`}></XIcon>
        </div>
      </TwitterShareButton>
      <FacebookShareButton url={url}>
        <div
          className={`rounded-[50%] p-1 ${props.bgColor ? props.bgColor : 'bg-primary-light-grey'}`}
        >
          <FacebookIcon
            fill={`${props.fill ? props.fill : 'black'}`}
          ></FacebookIcon>
        </div>
      </FacebookShareButton>

      {copied &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="status"
            aria-live="polite"
            className="fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2 rounded-full bg-[#041118] px-5 py-3 font-inter text-sm font-medium text-white shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
          >
            Link copied to clipboard
          </div>,
          document.body,
        )}
    </div>
  );
};
